import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react'

import { reduce } from 'lodash'

import api from 'common/api'
import { useScid } from 'store/user/hooks'

import {
  supportingDocumentKeys,
  supportingDocumentsMap,
  supportingDocumentKeyMap,
  academicRecordKeys,
  academicRecordsMap,
  academicRecordKeyMap,
  transferRequirementKeys,
  transferRequirementsMap,
  transferRequirementKeyMap,
  RequirementValue,
  RoundRequirement,
} from './types'

type contextValueType = {
  requirements?: RoundRequirement[]
  applicationCycle: number
  fetchRequirements?: () => Promise<void>
  updateRequirements: (requirement: RoundRequirement) => Promise<void>
  deleteRequirements: (requirementId: string) => Promise<void>
}

const Context = React.createContext({} as contextValueType)

type academicRecords = {
  [key in academicRecordKeys]?: NonRecommendationDocument
}
type supportingDocuments = {
  [key in supportingDocumentKeys]?: NonRecommendationDocument
}
type transferRequirements = {
  [key in transferRequirementKeys]?: NonRecommendationDocument
}

type RecommendationRequirement = {
  RequiredCount: number
  OptionalSendStatus: RequirementValue
}
type NonRecommendationDocument = {
  Value: RequirementValue
}
type ApiRequirementRequirement = {
  Id: string
  Name: string
  ApplicantType?: 'firstYear' | 'transfer' | undefined
  UpdatedBy: string
  UpdatedAt: string
  UpdatedByName: string
  Recommendations: {
    Min: number
    Max: number
    Teacher: RecommendationRequirement
    Counselor: RecommendationRequirement
    Other: RecommendationRequirement
  }
  AcademicRecords: academicRecords
  SupportingDocuments: supportingDocuments
  TransferRequirements: transferRequirements
}
type RequirementApi = ApiRequirementRequirement[]

export const useRequirements = (): RoundRequirement[] => {
  const { requirements } = useContext(Context)
  return requirements ?? []
}
export const useFetchRequirements = () => {
  const { fetchRequirements } = useContext(Context)
  return fetchRequirements
}
export const useUpdateRequirements = () => {
  const { updateRequirements } = useContext(Context)
  return updateRequirements
}
export const useDeleteRequirements = () => {
  const { deleteRequirements } = useContext(Context)
  return deleteRequirements
}

const RoundRequirementProvider = ({
  applicationCycle,
  children,
}: {
  applicationCycle: number
  children: React.ReactNode
}) => {
  const [requirements, setRequirements] = useState<RoundRequirement[]>()
  const scid = useScid()
  const fetchRequirements = useCallback(
    () =>
      api
        .get<RequirementApi>(`/api/colleges/${scid}/document-requirements/${applicationCycle}`)
        .catch(() => [])
        .then((fetchedRequirements) => {
          const transformedRequirements = (fetchedRequirements ?? []).map((requirement: ApiRequirementRequirement) => ({
            id: requirement.Id,
            applicantType: requirement.ApplicantType,
            name: requirement.Name,
            updatedBy: requirement.UpdatedBy,
            updatedAt: requirement.UpdatedAt,
            updatedByName: requirement.UpdatedByName,
            recommendations: {
              min: requirement.Recommendations.Min,
              max: requirement.Recommendations.Max,
              teacher: requirement.Recommendations.Teacher,
              counselor: requirement.Recommendations.Counselor,
              other: requirement.Recommendations.Other,
            },
            academicRecords: reduce(
              academicRecordKeyMap,
              (reducerValue: academicRecordsMap, key: academicRecordKeys) => {
                reducerValue[key] = requirement.AcademicRecords?.[key]?.Value
                return reducerValue
              },
              {},
            ),
            supportingDocuments: reduce(
              supportingDocumentKeyMap,
              (reducerValue: supportingDocumentsMap, key: supportingDocumentKeys) => {
                reducerValue[key] = requirement.SupportingDocuments?.[key]?.Value
                return reducerValue
              },
              {},
            ),
            transferRequirements: reduce(
              transferRequirementKeyMap,
              (reducerValue: transferRequirementsMap, key: transferRequirementKeys) => {
                reducerValue[key] = requirement.TransferRequirements?.[key]?.Value
                return reducerValue
              },
              {},
            ),
          })) as RoundRequirement[]
          setRequirements(transformedRequirements)
        }),
    [scid, applicationCycle],
  )

  const deleteRequirements = useCallback(
    (requirementsId: string) =>
      api.delete(`/api/colleges/${scid}/document-requirements/${requirementsId}`).then(fetchRequirements),
    [scid, fetchRequirements],
  )

  const updateRequirements = useCallback(
    (requirements: RoundRequirement) => {
      const mappedRequirements = {
        Id: requirements.id,
        ApplicantType: requirements.applicantType,
        Name: requirements.name,
        UpdatedBy: requirements.updatedBy,
        UpdatedAt: requirements.updatedAt,
        UpdatedByName: requirements.updatedByName,
        CollegeId: scid,
        ApplicationCycle: applicationCycle,
        Recommendations: {
          Min: requirements.recommendations.min,
          Max: requirements.recommendations.max,
          Teacher: requirements.recommendations.teacher,
          Counselor: requirements.recommendations.counselor,
          Other: requirements.recommendations.other,
        },
        AcademicRecords: reduce(
          academicRecordKeyMap,
          (reducerValue: academicRecords, key: academicRecordKeys) => {
            reducerValue[key] = { Value: requirements.academicRecords?.[key] } as NonRecommendationDocument
            return reducerValue
          },
          {},
        ),
        SupportingDocuments: reduce(
          supportingDocumentKeyMap,
          (reducerValue: supportingDocuments, key: supportingDocumentKeys) => {
            reducerValue[key] = { Value: requirements.supportingDocuments?.[key] } as NonRecommendationDocument
            return reducerValue
          },
          {},
        ),
        TransferRequirements: reduce(
          transferRequirementKeyMap,
          (reducerValue: transferRequirements, key: transferRequirementKeys) => {
            reducerValue[key] = { Value: requirements.transferRequirements?.[key] } as NonRecommendationDocument
            return reducerValue
          },
          {},
        ),
      } as ApiRequirementRequirement
      const apiFn = mappedRequirements.Id ? api.put : api.post
      return apiFn(
        `/api/colleges/${scid}/document-requirements${mappedRequirements.Id ? `/${mappedRequirements.Id}` : ''}`,
        mappedRequirements,
      ).then(fetchRequirements)
    },
    [scid, applicationCycle, fetchRequirements],
  )

  useEffect(() => {
    fetchRequirements()
  }, [fetchRequirements])

  const providerValue = useMemo(
    () => ({
      requirements: requirements ?? [],
      applicationCycle,
      fetchRequirements,
      updateRequirements,
      deleteRequirements,
    }),
    [requirements, applicationCycle, fetchRequirements, updateRequirements, deleteRequirements],
  )
  return <Context.Provider value={providerValue}>{children}</Context.Provider>
}

export default RoundRequirementProvider
